// Story 7.12 Iteration 2 (ADO-911) — PLACEHOLDER sample values for the Product
// Detail Page.
//
// The PO-approved PDP design (Figma 1169:7024 / 1193:7661) surfaces a large set
// of commercial / master-data / EPOS fields that NO producer exists for yet. Per
// OQ-10 and OQ-13 (both RESOLVED: "ship the hero + accordion display fields as
// FIXTURES") every field below is a DISPLAY-ONLY SEAM, not real data — exactly
// the `productCardSample.ts` posture the grid card already ships.
//
// Deviation D-1: the platform cannot source RRP or margin, so those values here are
// placeholders. The sample multi-buy offer is deleted (Pricing migration 32, ADO-3985 T10):
// the real deals come from `useMultibuyDealsStore`. The hero's unit price is not one of them: it reads
// `provisionalUnitPrice`, so this fixture carries no price.
//
// The gap register in the story maps each field to the story that will own it:
//   RRP                        → Pricing migration 17 (ADO-2248): real with VITE_REAL_PRICES_ENABLED on
//   Offers / promotions        → Pricing migration 18 (ADO-2249); multi-buy → Pricing migration 32 (ADO-3985)
//   WOW                        → Story 11.12 (ADO-1963)
//   Margin                     → Story 11.10 (ADO-1961), G4: the price quote's with
//                                VITE_REAL_PRICES_ENABLED on, this sample with it off
//   POR (Profit on Return)     → its own item, G9: Story 11.10 (ADO-1961) took it off the page
//                                in both flag states until it is defined; no sample is kept
// RPS is GONE: the PO removed it from the designs on 2026-07-29, closing gap G3.
//   Nutrition                  → 14.14 ingest + 7.17 display, G1
//   "Your section"             → Epic 8 section context, G8
// Swap each fixture for the real source as its producer lands.
//
// Story 7.15 (ADO-1372) did exactly that for gaps G5 + G6: Code / EAN / VAT /
// Origin and the breadcrumb category path are REAL now (`ProductDetail` on the
// by-SKU read) and their fixtures are deleted, not merely unused.
//
// Story 7.16 (ADO-1373) did the same for gap G2: the whole `stockCard` sub-object
// — `week`, `soldLast7d`, `depots`, `lastOrderedAgo` — is GONE, along with the
// `StockCardWeekRow` / `DepotStockRow` types, the `depotRow` helper and the
// `DAY_LABELS` export that only the week table used. The week table and the
// "sold (7d)" badge now read Story 14.15's real store EPOS movement
// (`stock-card.client.ts`); the depot rows were DELETED rather than re-sourced,
// because no read model exposes a per-environment breakdown (Deviation D-2), and
// `lastOrderedAgo` went with them — a fabricated "N days ago" has no place beside
// live figures in the same component.
//
// Story 8.12 (ADO-2021) did the same for the hero's On Order row: it reads the
// Customer's open orders (`useOnOrderFor`), and the `onOrder` fixture and its
// type are deleted.
//
// The hero's `lastOrdered` fixture is deleted too (Bug 3737): a made-up date read as
// a real one. Its Last Ordered cell shows the empty-cell glyph until Story 7.23
// (ADO-1712) wires the real date.

export interface ProductDetailSample {
  // ── Hero (D-1: no producer) ──
  readonly rrp: string;
  readonly packCase: string;
  readonly marginPct: string;
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

// Hoisted, not constructed per call: `Intl.NumberFormat` is expensive to build
// and this module is called from every card, tile and modal render.
const EUR = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' });
const eur = (n: number): string => EUR.format(n);

/**
 * Deterministic placeholder PDP data for a SKU (stable across renders, and
 * stable across runs — no `Date.now()`, no randomness). Mirrors
 * `sampleCardData` so the PDP and the grid card tell the same story for a SKU.
 */
export function productDetailSample(sku: string): ProductDetailSample {
  const h = hash(sku);
  // The placeholder buying price the RRP below is derived from (same basis
  // as the card's RRP). Not returned: the hero shows the provisional unit price.
  const price = 1.5 + (h % 1600) / 100; // €1.50–€17.49
  const marginPct = 6 + (h % 1100) / 100;
  const rrp = price * (1 + marginPct / 100 + 0.02);
  const packQty = 3 + (h % 10);
  // Placeholder pack WEIGHT for the hero's Pack/Case cell. `packCase` previously
  // interpolated the SKU here, so the cell read "7 / 58384" on any product whose
  // read-model row has no pack (the only case where the fixture is reached at all).
  const packWeightG = 800 + (h % 5) * 50;

  return {
    rrp: eur(rrp),
    packCase: `${packQty} / ${packWeightG}g`,
    marginPct: marginPct.toFixed(2),
    // Story 7.15 (ADO-1372): `categoryPath` and `productInformation` are GONE from
    // this fixture — the breadcrumb (G6) and Code / EAN / VAT / Origin (G5) are now
    // REAL read-model data, sourced through `ProductDetail` on the by-SKU read. The
    // hashed `Code` in particular was WRONG DATA rather than placeholder data: it is
    // the value a customer quotes to order, and it is simply the SKU (D-2).
    //
    // The `Available` window went with them, and does NOT come back here: the PO's
    // 2026-07-30 answer scopes the ordering window to products ON OFFER, and SL1.0's
    // own Start/End rows are `promStart`/`promEnd` — a promotion window. It belongs
    // to Story 11.5 (#1109) with its offer, not to Product Information (D-3).
  };
}
